import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { getCourses, createCourse, deleteCourse, getLecturers, checkCourseConflicts } from '../services/api';
import EmptyState from '../components/EmptyState';

export default function Courses() {
  const { t, lang } = useLang();
  const { userName } = useUser();
  const [courses, setCourses] = useState<any[]>([]);
  const [lecturers, setLecturers] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [conflicts, setConflicts] = useState<any[]>([]);
  const [checking, setChecking] = useState(false);
  const [showConflictWarning, setShowConflictWarning] = useState(false);
  const [form, setForm] = useState({
    name: '', nameAr: '', code: '', lecturerId: '', semester: 'A',
    year: '2025-2026', maxStudents: 30, price: 0, description: '',
    schedule: [{ day: 0, startTime: '09:00', endTime: '10:30', room: '' }],
    performedBy: ''
  });

  const loadData = () => {
    getCourses({ search: search || undefined }).then(res => setCourses(res.data)).catch(() => {});
    getLecturers().then(res => setLecturers(res.data)).catch(() => {});
  };

  useEffect(() => { loadData(); }, [search]);

  const doSave = async () => {
    await createCourse(form);
    setShowModal(false);
    setShowConflictWarning(false);
    setConflicts([]);
    setChecking(false);
    loadData();
  };

  const handleSaveClick = async () => {
    setChecking(true);
    try {
      const res = await checkCourseConflicts({
        lecturerId: form.lecturerId,
        schedule: form.schedule,
        name: form.name,
        nameAr: form.nameAr
      });
      if (res.data.hasConflicts) {
        setConflicts(res.data.conflicts);
        setShowConflictWarning(true);
        setChecking(false);
      } else {
        await doSave();
      }
    } catch {
      await doSave();
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('האם למחוק?')) {
      await deleteCourse(id);
      loadData();
    }
  };

  const getLecturerName = (id: string) => {
    const l = lecturers.find(lec => lec.id === id);
    if (!l) return id;
    if (lang === 'ar' && l.firstNameAr) return `${l.firstNameAr} ${l.lastNameAr}`;
    return `${l.firstName} ${l.lastName}`;
  };

  const days = [t('sunday'), t('monday'), t('tuesday'), t('wednesday'), t('thursday'), t('friday')];

  return (
    <div>
      <div className="page-title">
        <span className="page-title-text">📚 {t('courseList')}</span>
        <button className="btn btn-primary" onClick={() => { setForm({...form, performedBy: userName}); setShowModal(true); }}>+ {t('addCourse')}</button>
      </div>

      <div className="page-section">
        <div className="toolbar">
          <input className="search-input" placeholder={t('search')} value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('courseCode')}</th>
              <th>{t('courseName')}</th>
              <th>{t('lecturer')}</th>
              <th>{t('semester')}</th>
              <th>{t('enrolled')}</th>
              <th>{t('price')}</th>
              <th>{t('performedBy')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {courses.map(c => (
              <tr key={c.id}>
                <td><strong>{c.code}</strong></td>
                <td>
                  <Link to={`/courses/${c.id}`} style={{ color: '#7B1FA2', fontWeight: 600, textDecoration: 'none' }}>
                    {lang === 'ar' && c.nameAr ? c.nameAr : c.name}
                  </Link>
                </td>
                <td>{getLecturerName(c.lecturerId)}</td>
                <td>{c.semester} - {c.year}</td>
                <td>{c.enrolledStudents.length}/{c.maxStudents}</td>
                <td>{c.price.toLocaleString()} ₪</td>
                <td>{c.performedBy || '-'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Link to={`/courses/${c.id}`} className="btn btn-sm btn-outline">{t('details')}</Link>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(c.id)}>{t('delete')}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {courses.length === 0 && (
          <EmptyState
            icon="📚"
            title={t('emptyCourses')}
            actionLabel={t('addCourse')}
            onAction={() => { setForm({...form, performedBy: userName}); setShowModal(true); }}
          />
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('addCourse')}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('courseName')} (עברית)</label>
                <input className="form-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('courseName')} (ערבית)</label>
                <input className="form-input" value={form.nameAr} onChange={e => setForm({...form, nameAr: e.target.value})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('courseCode')}</label>
                <input className="form-input" value={form.code} onChange={e => setForm({...form, code: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('lecturer')}</label>
                <select className="form-select" value={form.lecturerId} onChange={e => setForm({...form, lecturerId: e.target.value})}>
                  <option value="">-- בחר --</option>
                  {lecturers.map(l => (
                    <option key={l.id} value={l.id}>{l.firstName} {l.lastName}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('semester')}</label>
                <select className="form-select" value={form.semester} onChange={e => setForm({...form, semester: e.target.value})}>
                  <option value="A">סמסטר א׳</option>
                  <option value="B">סמסטר ב׳</option>
                  <option value="summer">קיץ</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('maxStudents')}</label>
                <input className="form-input" type="number" value={form.maxStudents} onChange={e => setForm({...form, maxStudents: parseInt(e.target.value)})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('price')} (₪)</label>
                <input className="form-input" type="number" value={form.price} onChange={e => setForm({...form, price: parseInt(e.target.value)})} />
              </div>
            </div>
            <h4 style={{ marginBottom: 8, color: '#7B1FA2' }}>{t('weeklySchedule')}</h4>
            {form.schedule.map((s, i) => (
              <div className="form-row" key={i}>
                <div className="form-group">
                  <label className="form-label">{t('day')}</label>
                  <select className="form-select" value={s.day} onChange={e => {
                    const newSch = [...form.schedule];
                    newSch[i] = {...newSch[i], day: parseInt(e.target.value)};
                    setForm({...form, schedule: newSch});
                  }}>
                    {days.map((d, di) => <option key={di} value={di}>{d}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">שעת התחלה</label>
                  <input className="form-input" type="time" value={s.startTime} onChange={e => {
                    const newSch = [...form.schedule];
                    newSch[i] = {...newSch[i], startTime: e.target.value};
                    setForm({...form, schedule: newSch});
                  }} />
                </div>
                <div className="form-group">
                  <label className="form-label">שעת סיום</label>
                  <input className="form-input" type="time" value={s.endTime} onChange={e => {
                    const newSch = [...form.schedule];
                    newSch[i] = {...newSch[i], endTime: e.target.value};
                    setForm({...form, schedule: newSch});
                  }} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('room')}</label>
                  <input className="form-input" value={s.room} onChange={e => {
                    const newSch = [...form.schedule];
                    newSch[i] = {...newSch[i], room: e.target.value};
                    setForm({...form, schedule: newSch});
                  }} />
                </div>
              </div>
            ))}
            <button className="btn btn-sm btn-outline" style={{ marginBottom: 12 }} onClick={() => setForm({...form, schedule: [...form.schedule, { day: 0, startTime: '09:00', endTime: '10:30', room: '' }]})}>
              + הוסף שעה
            </button>
            <div className="form-group">
              <label className="form-label">{t('actionPerformer')}</label>
              <input className="form-input" value={form.performedBy} onChange={e => setForm({...form, performedBy: e.target.value})} />
            </div>
            {showConflictWarning && (
              <div style={{ border: '2px solid #B71C1C', background: '#fff3e0', color: '#B71C1C', borderRadius: 8, padding: 16, marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 10 }}>⚠️ {t('conflictTitle')}</div>
                <ul style={{ margin: 0, paddingInlineStart: 20 }}>
                  {conflicts.map((c, i) => (
                    <li key={i} style={{ marginBottom: 8 }}>
                      <strong>{c.type === 'lecturer' ? t('conflictLecturer') : t('conflictRoom')}</strong>
                      {' — '}{days[c.day]} {c.candidateSlot.startTime}-{c.candidateSlot.endTime}
                      <br />
                      {t('conflictWith')}: {lang === 'ar' ? c.courseNameAr : c.courseName}
                      {c.room ? ` (${t('room')}: ${c.room})` : ''}
                      {' '}({c.otherSlot.startTime}-{c.otherSlot.endTime})
                    </li>
                  ))}
                </ul>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="btn btn-danger" onClick={doSave}>{t('saveAnyway')}</button>
                  <button className="btn btn-outline" onClick={() => setShowConflictWarning(false)}>{t('cancel')}</button>
                </div>
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleSaveClick} disabled={checking}>{checking ? t('checkingConflicts') : t('save')}</button>
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
